import React from "react";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { money } from "./format";

function FlowNode({ label, color, value, sub, isNegative }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        minWidth: 0,
        borderColor: isNegative ? "error.main" : color,
        borderLeftWidth: 3,
      }}
    >
      <Typography variant="overline" color="text.secondary" noWrap sx={{ display: "block", lineHeight: 1.6 }}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        noWrap
        sx={{ fontVariantNumeric: "tabular-nums", color: isNegative ? "error.main" : "text.primary" }}
      >
        {money(value)}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap component="div">
        {sub}
      </Typography>
    </Paper>
  );
}

const Arrow = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled" }}>
    <ArrowForwardIcon fontSize="small" />
  </Box>
);

/**
 * Where the money stands on the cursor day, left to right the way it moves:
 * the client pays Chinook, Chinook pays vendors and payroll, and what's left
 * of each collected packet is margin.
 *
 * @param position positionOn() result (cashTimeline.js)
 * @param totals   the timeline's totals for the lines in scope
 */
export default function CashFlowStrip({ position, totals }) {
  const { palette, breakpoints } = useTheme();
  const isNarrow = useMediaQuery(breakpoints.down("md"));
  const isFunding = position.cushion < -1;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        gridTemplateColumns: isNarrow
          ? "repeat(2, minmax(0, 1fr))"
          : "minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
      }}
    >
      <FlowNode
        label="Client has paid"
        color={palette.success.main}
        value={position.collected}
        sub={`${money(position.billed)} billed of ${money(totals.contract)}`}
      />
      {!isNarrow && <Arrow />}
      <FlowNode
        label="Chinook is holding"
        color={palette.primary.main}
        value={position.cushion}
        isNegative={isFunding}
        sub={isFunding ? "Chinook is funding the client" : "Cash cushion on this project"}
      />
      {!isNarrow && <Arrow />}
      <FlowNode
        label="Vendors paid"
        color={palette.warning.main}
        value={position.vendorPaid}
        sub={`of ${money(totals.vendorCost)}`}
      />
      <FlowNode
        label="Payroll paid"
        color={palette.secondary.main}
        value={position.payrollPaid}
        sub={`of ${money(totals.payrollCost)}`}
      />
      <FlowNode
        label="Margin secured"
        color={palette.info.main}
        value={position.margin}
        sub={`of ${money(totals.contract - totals.cost)} planned`}
      />
    </Box>
  );
}
